import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { createTeacherSupportRequest } from "../services/TeacherAPIClient";
import {
  buildTeacherSupportRequestPayload,
  getSupportRequestErrorMessage,
  getSupportRequestReference,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import "./TeacherEscalateFeatureRequestModal.css";

const areas = [
  { label: "Results and reports", detail: "Class-wide reporting or result views" },
  { label: "Class setup", detail: "Creating, sharing, or moving classes" },
  { label: "Screeners", detail: "Assessment setup or timing" },
  { label: "Other", detail: "Something else" }
];

const followUps = [
  { label: "You can contact me about this idea or related beta testing", value: "You can contact me", detail: "I am happy to help test related features." },
  { label: "Add me to the WhatsApp group for feature help", value: "Add me to the WhatsApp group", detail: "Use this if ALPACA wants teacher feedback on related features." },
  { label: "No follow-up needed", value: "No follow-up needed", detail: "Just capture my feedback" }
];

const suggestedResourceByArea = {
  "Results and reports": {
    title: "Results and reports guidance",
    type: "iframe"
  },
  "Class setup": {
    title: "Class setup guidance",
    type: "iframe"
  },
  Screeners: {
    title: "Screener setup guidance",
    type: "pdf"
  },
  Other: {
    title: "ALPACA help guidance",
    type: "iframe"
  }
};

function TeacherEscalateFeatureRequestModal({
  show,
  handleClose,
  prefill = null,
  schoolid = null,
  teacherEmail = "",
  teacherName = "",
  onOpenGuidance = null,
  onReturnToTeacherDashboard = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [featureArea, setFeatureArea] = useState("");
  const [featureIdea, setFeatureIdea] = useState("");
  const [benefit, setBenefit] = useState("");
  const [followUpPreference, setFollowUpPreference] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportReference, setSupportReference] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);

  useEffect(() => {
    if (!show) return;
    setStage("form");
    setFeatureArea(prefill?.featureArea || "");
    setFeatureIdea(prefill?.featureIdea || "");
    setBenefit(prefill?.benefit || "");
    setFollowUpPreference(prefill?.followUpPreference || "");
    setError("");
    setIsSubmitting(false);
    setSupportReference("");
    setIsDuplicatePending(false);
  }, [show, prefill]);

  const canContinue = featureArea && featureIdea.trim() && benefit.trim() && followUpPreference;
  const suggestedResource = suggestedResourceByArea[featureArea] || suggestedResourceByArea.Other;
  const openSuggestedGuidance = () => {
    if (onOpenGuidance) {
      onOpenGuidance({
        guidanceQuery: featureIdea || featureArea || suggestedResource.title
      });
      return;
    }

    handleClose();
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference,
        message: "Feature request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const returnToTeacherDashboard = () => {
    if (embedded) {
      handleDone();
      return;
    }

    if (onReturnToTeacherDashboard) {
      onReturnToTeacherDashboard();
      return;
    }

    handleClose();
  };

  const sendFeatureRequest = async () => {
    if (!canContinue || isSubmitting) return;
    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const payload = buildTeacherSupportRequestPayload({
      type: "product_feedback",
      schoolId: schoolid,
      details: {
        featureArea,
        featureIdea,
        benefit,
        followUpPreference,
        className: prefill?.className,
        schoolName: prefill?.schoolName,
        teacherName,
        email: prefill?.email || teacherEmail
      },
      source: {
        workflow: "escalate-feature-request"
      }
    });

    try {
      const response = await createTeacherSupportRequest(payload);
      setSupportReference(getSupportRequestReference(response));
      setIsDuplicatePending(isDuplicatePendingResponse(response));
      setStage("success");
    } catch (submitError) {
      setError(getSupportRequestErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderOptionCard = ({ label, value = label, detail }, selectedValue, setValue, name) => (
    <label key={value} className={`feature-request-option${selectedValue === value ? " selected" : ""}`}>
      <Form.Check
        type="radio"
        name={name}
        checked={selectedValue === value}
        onChange={() => setValue(value)}
        aria-label={label}
      />
      <span>
        <strong>{label}</strong>
        <span>{detail}</span>
      </span>
    </label>
  );

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-feature-request-modal">
      {stage === "form" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Suggest an Improvement</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="feature-request-intro">Tell us what would make ALPACA work better for your school.</p>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="feature-request-section">
              <Form.Label>Which area is this about?</Form.Label>
              <div className="feature-request-options">
                {areas.map((option) => renderOptionCard(option, featureArea, setFeatureArea, "featureArea"))}
              </div>
            </Form.Group>

            <Form.Group className="feature-request-section" controlId="featureIdea">
              <Form.Label>What would you like?</Form.Label>
              <Form.Text as="div" className="feature-request-field-label">Feature idea</Form.Text>
              <Form.Control
                value={featureIdea}
                onChange={(event) => setFeatureIdea(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="feature-request-section" controlId="featureBenefit">
              <Form.Label>Why would this help?</Form.Label>
              <Form.Text as="div" className="feature-request-field-label">Benefit</Form.Text>
              <Form.Control
                value={benefit}
                onChange={(event) => setBenefit(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="feature-request-section">
              <Form.Label>Can we contact you?</Form.Label>
              <div className="feature-request-options">
                {followUps.map((option) => renderOptionCard(option, followUpPreference, setFollowUpPreference, "featureFollowUp"))}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <span className="feature-request-footer-note">{canContinue ? "Feature request ready." : "Add the idea and benefit."}</span>
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={() => setStage("existing")} disabled={!canContinue}>Send idea</Button>
          </Modal.Footer>
        </>
      )}
      {stage === "existing" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">This May Already Exist</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="feature-request-intro">A current feature or resource may help with this request.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning" className="feature-request-warning">
              <strong>{suggestedResource.title}</strong>
              <span>
                This is matched to "{featureArea}" and your request: {featureIdea}. Your feedback will still be captured if it does not solve what you need.
              </span>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <span className="feature-request-footer-note">Resource suggested.</span>
            <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
            <Button variant="outline-primary" onClick={openSuggestedGuidance}>Open guidance</Button>
            <Button variant="primary" onClick={sendFeatureRequest} disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send feedback anyway"}
            </Button>
          </Modal.Footer>
        </>
      )}
      {stage === "success" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Feedback Captured</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="feature-request-intro">We may contact you about this idea or related beta testing.</p>
            <Alert variant="success" className="feature-request-success">
              <span className="feature-request-ok-badge" aria-hidden="true">OK</span>
              <span>
                <strong>
                  {isDuplicatePending
                    ? "This feature feedback is already in progress."
                    : "Thanks, your feature feedback has been sent."}
                </strong>
                <span>
                  {supportReference ? `Reference: ${supportReference}. ` : ""}
                  We may contact you about this idea or related beta testing.
                </span>
              </span>
            </Alert>
            <div className="feature-request-next">
              <strong>What next?</strong>
              <span>Open related guidance, or {embedded ? "close back to SENSAI" : "return to Teacher Dashboard"} while ALPACA reviews the feedback.</span>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="feature-request-footer-note">Task complete.</span>
            <Button variant="outline-secondary" onClick={openSuggestedGuidance}>Open guidance</Button>
            {!embedded && (
              <Button variant="outline-secondary" onClick={returnToTeacherDashboard}>Return to Teacher Dashboard</Button>
            )}
            <Button variant="primary" onClick={handleDone}>Close</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherEscalateFeatureRequestModal;
